import { useCallback, useMemo, useState } from 'react';
export type ChannelGroupRowDragEndEvent<T> = {
    api: {
        forEachNode: (callback: (node: { data?: T }) => void) => void;
    };
};

const withSequentialWeights = <T extends { _id: string; weight: number }>(
    items: T[]
): T[] =>
    items.map((group, index) => ({
        ...group,
        weight: index + 1,
    }));

const useChannelGroupRowDragging = <T extends { _id: string; weight: number }>(
    groups: T[]
) => {
    const [reorderedGroups, setReorderedGroups] = useState<T[] | null>(null);
    const [weightsDirty, setWeightsDirty] = useState(false);

    const onRowDragEnd = useCallback(
        (event: ChannelGroupRowDragEndEvent<T>) => {
            const reordered: T[] = [];

            event.api.forEachNode((node) => {
                if (node.data) {
                    reordered.push(node.data);
                }
            });

            setReorderedGroups(withSequentialWeights(reordered));
            setWeightsDirty(true);
        },
        []
    );

    const resetRowDragging = useCallback(() => {
        setReorderedGroups(null);
        setWeightsDirty(false);
    }, []);

    const channelGroups = useMemo(
        () => reorderedGroups || groups,
        [reorderedGroups, groups]
    );

    return {
        channelGroups,
        weightsDirty,
        onRowDragEnd,
        resetRowDragging,
    };
};

export default useChannelGroupRowDragging;
